/** The post-onboarding recommendation, shown on the dashboard.
 *
 * A self-serve learner who has finished onboarding but doesn't yet own their
 * recommended level sees "we suggest B1 — Complete" with a link into the
 * store, pre-pointed at that package. Once they own it, the card disappears
 * (no nagging). Staff and not-yet-onboarded users never see it.
 *
 * The copy is composed client-side from the structured answers so it is
 * fully bilingual — the backend engine's English strings are for API
 * consumers; the UI speaks the viewer's language.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getOnboarding, type Recommendation } from "../../api/onboarding";
import { getStore, type Entitlements } from "../../api/store";
import type { OnboardingAnswers } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";

const REASON_KEY: Record<string, MessageKey> = {
  exam: "dashboard.rec.reason.exam",
  work: "dashboard.rec.reason.work",
  study: "dashboard.rec.reason.study",
  immigration: "dashboard.rec.reason.immigration",
  travel: "dashboard.rec.reason.travel",
};

function ownsComplete(ent: Entitlements | null, level: string): boolean {
  if (!ent) return false;
  const lv = level.toUpperCase();
  const has = (list: string[]) => list.some((l) => l.toUpperCase() === lv);
  return has(ent.learn) && has(ent.practice);
}

export function RecommendationCard() {
  const t = useT();
  const { user } = useAuth();
  const [rec, setRec] = useState<Recommendation | null>(null);
  const [answers, setAnswers] = useState<OnboardingAnswers>({});
  const [ent, setEnt] = useState<Entitlements | null>(null);

  const active = Boolean(user?.is_self_serve && user?.onboarded);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    getOnboarding()
      .then((state) => {
        if (cancelled) return;
        setRec(state.recommendation);
        setAnswers(state.answers);
      })
      .catch(() => {
        // No recommendation yet → the card simply doesn't render.
      });
    getStore()
      .then((store) => {
        if (!cancelled) setEnt(store.entitlements);
      })
      .catch(() => {
        // Store unavailable → assume nothing owned, still offer the plan.
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  if (!rec || ownsComplete(ent, rec.level)) return null;

  const board = answers.board ?? "either";
  const boardLabel =
    board === "telc" ? "telc" : board === "goethe" ? "Goethe" : t("dashboard.rec.bothBoards");
  const reasonKey = REASON_KEY[answers.goal ?? "travel"] ?? REASON_KEY.travel;

  return (
    <div className="bg-grad-bronze rounded-card p-6 text-white shadow-lg">
      <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
        {t("dashboard.rec.eyebrow")}
      </p>
      <h2 className="mt-1 text-xl font-extrabold">
        {t("dashboard.rec.headline", { level: rec.level })}
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-white/90">
        {t(reasonKey, { level: rec.level, board: boardLabel })}
      </p>
      {rec.add_credits && (
        <p className="mt-1 text-sm text-white/75">{t("dashboard.rec.credits")}</p>
      )}
      <Link
        to={`/store?rec=${encodeURIComponent(rec.pack_code)}`}
        className="mt-4 inline-block rounded-control bg-white px-5 py-2.5 text-sm font-bold text-bronze-dark shadow transition-transform hover:-translate-y-0.5"
      >
        {t("dashboard.rec.cta")} →
      </Link>
    </div>
  );
}
